import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import Modal from "components/modal/Modal";
import { RetrievePalResponseDto, useDownloadedDocumentsLogMutation, useRetrievePalContentQuery, useViewedDocumentsLogMutation } from "../dashboardService";
import { useSelector } from "react-redux";
import { getCurrentUserId, getLoanId, getNhDealApplicationId } from "features/authSlice";
import Overlay from "components/ui/loading-spinners/Overlay/Overlay";
import { Button } from "components/ui/button/Button";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./preApprovalPreview.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { shareFiles } from "utilities/files";
import { base64ToUint8Array } from "utilities/file";
import { PalDocumentDefaultName } from "utilities/Constant";
import { useEffect, useState } from "react";

interface PreviewProps {
  readonly onClose?: () => void;
  readonly palDocument?: RetrievePalResponseDto;
}
export default function PreApprovalPreview({ onClose, palDocument }: PreviewProps) {
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const userId = useSelector(getCurrentUserId);
  const [updateDownloadedDocuments] = useDownloadedDocumentsLogMutation();
  const [updateViewedDocuments] = useViewedDocumentsLogMutation();
  const loanId = useSelector(getLoanId);
  const shouldSkipQuery = !!palDocument;
  const [documentUrl, setDocumentUrl] = useState<string>("");

  const {
    data: retrievePalData,
    isLoading: isRetreivePalLoading,
    isSuccess: isRetreivePalSuccess,
  } = useRetrievePalContentQuery(
    { nhDealApplicationId, loanId },
    {
      skip: shouldSkipQuery,
    }
  );

  let currentDocument: RetrievePalResponseDto | undefined;

  if (palDocument) {
    currentDocument = palDocument;
  } else if (isRetreivePalSuccess) {
    currentDocument = retrievePalData[0];
  }

  useEffect(() => {
  if (!palDocument && isRetreivePalSuccess && retrievePalData?.[0]) {
    void updateViewedDocuments({userId, documentIds: [retrievePalData[0].palInstanceId],
    });
  }}, [palDocument, isRetreivePalSuccess, retrievePalData, updateViewedDocuments, userId]);
  
  useEffect(() => {
  if (!currentDocument) {
    setDocumentUrl("");
    return;
  }
  const blob = new Blob(
    [base64ToUint8Array(currentDocument.documentContent?.fileContents ?? "")],
    { type: "application/pdf" }
  );
  const url = globalThis.URL.createObjectURL(blob);
  setDocumentUrl(url);

  return () => {
    globalThis.URL.revokeObjectURL(url);
  };
}, [currentDocument]);

  const handleSharePAL = async () => {
    try {
      const blob = new Blob([base64ToUint8Array(currentDocument?.documentContent?.fileContents ?? "")], {
        type: "application/pdf",
      });
      const file = new File([blob], currentDocument?.documentContent?.name ?? PalDocumentDefaultName, { type: "application/pdf" });
      await shareFiles({
        files: [file]
      } as ShareData);
    } catch (error) {
      console.error("Error preparing file for sharing:", error);
    }
  };

  const docs = [{ uri: documentUrl, fileName: currentDocument?.documentContent?.name ?? "" }];

  if (isRetreivePalLoading) {
    return <Overlay />;
  }

  return (
    <div className="preApproval">
      <Modal
        defaultHeader
        defaultHeaderIcon={solid("file-lines")}
        defaultHeaderName="Pre-Approval Letter"
        defaultHandleHide={onClose}
        defaultBody={false}
        footerContentPosition="right"
        customBody={
          <>
            {documentUrl ? (
              <>
                <div className="topSplitter" />
                <DocViewer documents={docs} pluginRenderers={DocViewerRenderers} />
              </>
            ) : (
              <h1>Oops! There's a problem viewing this document.</h1>
            )}
          </>
        }
        footer={
          <div className="previewFooter">
            <a href={documentUrl}
              download={currentDocument?.documentContent?.name}
              onClick={() => {
                if (currentDocument) {
                  void updateDownloadedDocuments({ userId, documentIds: [currentDocument.palInstanceId] });
                }
              }
              }
              target="_blank" rel="noreferrer">
              <Button title={"Download"} type={"round"} variant={"outline-primary"} />
            </a>
            <Button type={"round"} variant={"primary"} onClick={handleSharePAL} className="sendPAL">
              <>
                <FontAwesomeIcon className="sendIcon" icon={solid("up-from-bracket")} /> Send Pre-Approval Letter
              </>
            </Button>
          </div>
        }
      />
    </div >
  );
}
